import React, { useState, useEffect, useRef } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Plus, Trash2, Edit2, X, Music, UploadCloud, Search, Play, Pause, FileAudio } from "lucide-react";
import Swal from "sweetalert2";
import { API_BASE_URL } from "../config";

export default function MusicManagement() {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [musicList, setMusicList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // Modal state for Add / Edit
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingMusic, setEditingMusic] = useState(null); // null for add, object for edit
    const [musicName, setMusicName] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewAudioUrl, setPreviewAudioUrl] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    // Bulk Upload Modal state
    const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
    const [bulkTracks, setBulkTracks] = useState([]); // array of { id, file, name, size, previewUrl }
    const [isBulkSaving, setIsBulkSaving] = useState(false);
    const bulkFileInputRef = useRef(null);

    // Audio Playback state
    const [currentlyPlayingId, setCurrentlyPlayingId] = useState(null);
    const audioRef = useRef(null);

    // 1. LIST MUSIC
    const fetchMusic = () => {
        setLoading(true);
        fetch(`${API_BASE_URL}/api/music/list`)
            .then((res) => {
                if (!res.ok) throw new Error("Failed to fetch music");
                return res.json();
            })
            .then((resJson) => {
                if (resJson && resJson.status && resJson.data) {
                    setMusicList(resJson.data);
                } else {
                    setMusicList([]);
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching music:", err);
                setMusicList([]);
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchMusic();
    }, []);

    const getNextMusicNumber = () => {
        let maxNum = 0;
        musicList.forEach((m) => {
            const match = (m.name || "").match(/music\s*(\d+)/i);
            if (match) {
                const num = parseInt(match[1], 10);
                if (num > maxNum) maxNum = num;
            }
        });
        return maxNum > 0 ? maxNum + 1 : (musicList.length + 1);
    };

    const handleFileChange = (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        // If multiple files selected, switch to bulk upload
        if (files.length > 1) {
            setIsModalOpen(false);
            handleBulkFilesSelect(files);
            setIsBulkModalOpen(true);
            return;
        }

        const file = files[0];
        if (file) {
            const allowed = ["audio/mpeg", "audio/mp3", "audio/wav", "audio/x-wav", "audio/ogg", "audio/mp4", "audio/aac", "audio/m4a", "audio/x-m4a"];
            const isAudio = file.type.startsWith("audio/") || allowed.includes(file.type) || /\.(mp3|wav|ogg|m4a|aac)$/i.test(file.name);
            if (!isAudio) {
                Swal.fire("Invalid File", "Please select an audio file (MP3, WAV, M4A, AAC, OGG).", "warning");
                return;
            }
            setSelectedFile(file);
            setPreviewAudioUrl(URL.createObjectURL(file));

            if (!musicName.trim()) {
                setMusicName(`Music ${getNextMusicNumber()}`);
            }
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        if (!files || files.length === 0) return;

        // If multiple files dropped, switch to bulk upload
        if (files.length > 1) {
            setIsModalOpen(false);
            handleBulkFilesSelect(files);
            setIsBulkModalOpen(true);
            return;
        }

        if (files[0]) {
            const file = files[0];
            const allowed = ["audio/mpeg", "audio/mp3", "audio/wav", "audio/x-wav", "audio/ogg", "audio/mp4", "audio/aac", "audio/m4a", "audio/x-m4a"];
            const isAudio = file.type.startsWith("audio/") || allowed.includes(file.type) || /\.(mp3|wav|ogg|m4a|aac)$/i.test(file.name);
            if (!isAudio) {
                Swal.fire("Invalid File", "Please drop an audio file (MP3, WAV, M4A, AAC, OGG).", "warning");
                return;
            }
            setSelectedFile(file);
            setPreviewAudioUrl(URL.createObjectURL(file));

            if (!musicName.trim()) {
                setMusicName(`Music ${getNextMusicNumber()}`);
            }
        }
    };

    // Bulk Files Processing
    const handleBulkFilesSelect = (filesList) => {
        if (!filesList || filesList.length === 0) return;
        const allowed = ["audio/mpeg", "audio/mp3", "audio/wav", "audio/x-wav", "audio/ogg", "audio/mp4", "audio/aac", "audio/m4a", "audio/x-m4a"];
        const newTracks = [];
        let rejectedCount = 0;

        let currentNextNum = getNextMusicNumber();
        bulkTracks.forEach((t) => {
            const match = (t.name || "").match(/music\s*(\d+)/i);
            if (match) {
                const num = parseInt(match[1], 10);
                if (num >= currentNextNum) currentNextNum = num + 1;
            }
        });

        Array.from(filesList).forEach((file, idx) => {
            const isAudio = file.type.startsWith("audio/") || allowed.includes(file.type) || /\.(mp3|wav|ogg|m4a|aac)$/i.test(file.name);
            if (!isAudio) {
                rejectedCount++;
                return;
            }
            newTracks.push({
                id: `${Date.now()}_${idx}_${Math.random()}`,
                file: file,
                name: `Music ${currentNextNum++}`,
                size: (file.size / (1024 * 1024)).toFixed(2),
                previewUrl: URL.createObjectURL(file)
            });
        });

        if (rejectedCount > 0) {
            Swal.fire("Non-Audio Files Skipped", `${rejectedCount} file(s) were skipped because they are not valid audio files.`, "info");
        }

        if (newTracks.length > 0) {
            setBulkTracks((prev) => [...prev, ...newTracks]);
        }
    };

    const handleBulkFileChange = (e) => {
        handleBulkFilesSelect(e.target.files);
        if (bulkFileInputRef.current) bulkFileInputRef.current.value = "";
    };

    const handleBulkDrop = (e) => {
        e.preventDefault();
        if (e.dataTransfer.files) {
            handleBulkFilesSelect(e.dataTransfer.files);
        }
    };

    const handleBulkTrackNameChange = (id, newName) => {
        setBulkTracks((prev) =>
            prev.map((t) => (t.id === id ? { ...t, name: newName } : t))
        );
    };

    const handleRemoveBulkTrack = (id) => {
        setBulkTracks((prev) => {
            const trackToRemove = prev.find((t) => t.id === id);
            if (trackToRemove && trackToRemove.previewUrl) {
                URL.revokeObjectURL(trackToRemove.previewUrl);
            }
            return prev.filter((t) => t.id !== id);
        });
    };

    const handleClearAllBulkTracks = () => {
        bulkTracks.forEach((t) => {
            if (t.previewUrl) URL.revokeObjectURL(t.previewUrl);
        });
        setBulkTracks([]);
    };

    const openBulkModal = () => {
        handleClearAllBulkTracks();
        setIsBulkModalOpen(true);
    };

    const closeBulkModal = () => {
        if (isBulkSaving) return;
        handleClearAllBulkTracks();
        setIsBulkModalOpen(false);
    };

    // Bulk Submit to API
    const handleBulkSubmit = async (e) => {
        e.preventDefault();
        if (bulkTracks.length === 0) {
            Swal.fire("No Files", "Please select at least one audio file to upload.", "warning");
            return;
        }

        setIsBulkSaving(true);
        try {
            const formData = new FormData();
            const namesArray = [];

            bulkTracks.forEach((t) => {
                formData.append("music", t.file);
                namesArray.push(t.name.trim() || t.file.name.replace(/\.[^/.]+$/, ""));
            });

            formData.append("names", JSON.stringify(namesArray));

            const res = await fetch(`${API_BASE_URL}/api/music/bulk-add`, {
                method: "POST",
                body: formData
            });

            const resJson = await res.json();

            if (res.ok && resJson.status) {
                Swal.fire({
                    icon: "success",
                    title: "Bulk Upload Complete!",
                    text: resJson.message || `${bulkTracks.length} music tracks added successfully as separate rows!`,
                    timer: 2500,
                    showConfirmButton: false
                });
                closeBulkModal();
                fetchMusic();
            } else {
                throw new Error(resJson.message || "Bulk upload failed");
            }
        } catch (err) {
            console.error("Bulk upload error:", err);
            Swal.fire("Upload Failed", err.message || "Failed to bulk upload music tracks.", "error");
        } finally {
            setIsBulkSaving(false);
        }
    };

    const openAddModal = () => {
        setEditingMusic(null);
        setMusicName(`Music ${getNextMusicNumber()}`);
        setSelectedFile(null);
        setPreviewAudioUrl("");
        setIsModalOpen(true);
    };

    const openEditModal = (music) => {
        setEditingMusic(music);
        setMusicName(music.name || "");
        setSelectedFile(null);
        setPreviewAudioUrl(music.music_url || "");
        setIsModalOpen(true);
    };

    const resetForm = () => {
        setEditingMusic(null);
        setMusicName("");
        setSelectedFile(null);
        if (previewAudioUrl && !editingMusic) URL.revokeObjectURL(previewAudioUrl);
        setPreviewAudioUrl("");
        setIsModalOpen(false);
    };

    // 2. ADD or EDIT SUBMIT
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!musicName.trim()) {
            Swal.fire("Required", "Please enter the music name.", "warning");
            return;
        }

        // If creating new music, audio file is required
        if (!editingMusic && !selectedFile) {
            Swal.fire("Audio File Required", "Please select an audio file to upload.", "warning");
            return;
        }

        setIsSaving(true);

        try {
            const formData = new FormData();
            formData.append("name", musicName.trim());
            if (selectedFile) {
                formData.append("music", selectedFile);
            }

            const url = editingMusic
                ? `${API_BASE_URL}/api/music/edit/${editingMusic.id || editingMusic.music_id_PK}`
                : `${API_BASE_URL}/api/music/add`;

            const method = editingMusic ? "PUT" : "POST";

            const res = await fetch(url, {
                method,
                body: formData
            });

            const resJson = await res.json();

            if (res.ok && resJson.status) {
                Swal.fire({
                    icon: "success",
                    title: editingMusic ? "Music Updated!" : "Music Added!",
                    text: resJson.message || "Saved successfully.",
                    timer: 2000,
                    showConfirmButton: false
                });
                resetForm();
                fetchMusic();
            } else {
                throw new Error(resJson.message || "Failed to save music");
            }
        } catch (err) {
            console.error("Save error:", err);
            Swal.fire("Error", err.message || "An error occurred while saving music.", "error");
        } finally {
            setIsSaving(false);
        }
    };

    // 3. DELETE MUSIC
    const handleDeleteMusic = (id, name) => {
        Swal.fire({
            title: `Delete "${name || 'Music'}"?`,
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#64748b",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    if (currentlyPlayingId === id && audioRef.current) {
                        audioRef.current.pause();
                        setCurrentlyPlayingId(null);
                    }

                    const res = await fetch(`${API_BASE_URL}/api/music/delete/${id}`, {
                        method: "DELETE"
                    });
                    const resJson = await res.json();
                    if (res.ok && resJson.status) {
                        Swal.fire({
                            icon: "success",
                            title: "Deleted!",
                            text: "Music deleted successfully.",
                            timer: 1500,
                            showConfirmButton: false
                        });
                        fetchMusic();
                    } else {
                        throw new Error(resJson.message || "Failed to delete music");
                    }
                } catch (err) {
                    console.error("Delete error:", err);
                    Swal.fire("Error", err.message || "Failed to delete music", "error");
                }
            }
        });
    };

    const togglePlayMusic = (id, url) => {
        if (!url) return;

        if (currentlyPlayingId === id) {
            if (audioRef.current) audioRef.current.pause();
            setCurrentlyPlayingId(null);
        } else {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = url;
                audioRef.current.play().catch((err) => console.log("Audio play error:", err));
            }
            setCurrentlyPlayingId(id);
        }
    };

    const filteredMusic = musicList.filter((m) => {
        if (!searchTerm) return true;
        const name = (m.name || "").toLowerCase();
        const id = String(m.id || m.music_id_PK || "");
        return name.includes(searchTerm.toLowerCase()) || id.includes(searchTerm);
    });

    const sortedMusic = [...filteredMusic].sort((a, b) => {
        const nameA = a.name || "";
        const nameB = b.name || "";
        return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
    });

    return (
        <div className="bg-[#f8fafc] dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-100 font-sans relative overflow-hidden">
            {/* Hidden audio element */}
            <audio
                ref={audioRef}
                onEnded={() => setCurrentlyPlayingId(null)}
                onError={() => setCurrentlyPlayingId(null)}
            />

            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

            <div className="flex-1 lg:ml-64 flex flex-col min-h-screen relative z-10 w-full lg:w-[calc(100%-16rem)]">
                <Header
                    onMenuClick={() => setIsSidebarOpen(true)}
                    title="Music Management"
                    subtitle="Manage background music (Add, Edit, Delete, List)."
                />

                <div className="p-4 sm:p-8 flex-1 w-full  mx-auto">
                    {/* Header Action Bar */}
                    <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2 bg-white dark:bg-slate-800 px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
                                <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Total Tracks:</span>
                                <span className="text-base font-bold text-blue-600 dark:text-blue-400">{musicList.length}</span>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                            {/* Search */}
                            <div className="relative">
                                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                <input
                                    type="text"
                                    placeholder="Search music..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="bg-white dark:bg-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl pl-9 pr-4 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all placeholder-slate-400"
                                />
                            </div>

                            {/* Bulk Add Music Button */}
                            <button
                                onClick={openBulkModal}
                                className="flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-4 py-2 rounded-xl font-medium shadow-md shadow-emerald-500/20 transition-all duration-200 active:scale-95 text-sm"
                            >
                                <UploadCloud className="w-4 h-4" />
                                <span>Bulk Add Music</span>
                            </button>

                            {/* Add Music Button */}
                            <button
                                onClick={openAddModal}
                                className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 py-2 rounded-xl font-medium shadow-md shadow-blue-500/20 transition-all duration-200 active:scale-95 text-sm"
                            >
                                <Plus className="w-4 h-4" />
                                <span>Add Music</span>
                            </button>
                        </div>
                    </div>

                    {/* Table / List */}
                    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center p-12 text-slate-400">
                                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4" />
                                <p className="text-sm">Loading music list...</p>
                            </div>
                        ) : sortedMusic.length === 0 ? (
                            <div className="flex flex-col items-center justify-center p-12 text-center text-slate-400">
                                <div className="p-4 bg-slate-100 dark:bg-slate-700/50 rounded-full mb-3">
                                    <Music className="w-8 h-8 text-slate-400" />
                                </div>
                                <h3 className="text-base font-semibold text-slate-700 dark:text-slate-200 mb-1">No Music Found</h3>
                                <p className="text-sm text-slate-400 max-w-sm mb-4">
                                    {searchTerm ? "No music matched your search." : "No music uploaded yet."}
                                </p>
                                {!searchTerm && (
                                    <button
                                        onClick={openAddModal}
                                        className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                                    >
                                        + Add Music
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                            <th className="py-4 px-6">#</th>
                                            <th className="py-4 px-6">Play</th>
                                            <th className="py-4 px-6">Music Name</th>
                                            <th className="py-4 px-6">Audio Player</th>
                                            <th className="py-4 px-6">Date</th>
                                            <th className="py-4 px-6 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50 text-sm">
                                        {sortedMusic.map((music, index) => {
                                            const musicId = music.id || music.music_id_PK;
                                            const isPlaying = currentlyPlayingId === musicId;
                                            return (
                                                <tr
                                                    key={musicId}
                                                    className="hover:bg-slate-50/80 dark:hover:bg-slate-700/30 transition-colors"
                                                >
                                                    <td className="py-4 px-6 text-slate-400 text-xs font-medium">
                                                        {index + 1}
                                                    </td>
                                                    <td className="py-4 px-6">
                                                        <button
                                                            onClick={() => togglePlayMusic(musicId, music.music_url)}
                                                            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${isPlaying
                                                                ? "bg-blue-600 text-white shadow-md shadow-blue-500/30 scale-105"
                                                                : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-slate-600"
                                                                }`}
                                                            title={isPlaying ? "Pause" : "Play"}
                                                        >
                                                            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                                                        </button>
                                                    </td>
                                                    <td className="py-4 px-6">
                                                        <div className="flex items-center gap-3">
                                                            <div className="p-2 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-lg">
                                                                <Music className="w-4 h-4" />
                                                            </div>
                                                            <div>
                                                                <span className="font-semibold text-slate-800 dark:text-slate-100 block">
                                                                    {music.name || `Music ${index + 1}`}
                                                                </span>
                                                                <span className="text-xs text-slate-400">ID: {musicId}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="py-4 px-6">
                                                        {music.music_url ? (
                                                            <audio
                                                                controls
                                                                preload="none"
                                                                src={music.music_url}
                                                                className="h-8 max-w-[260px] rounded-lg"
                                                            />
                                                        ) : (
                                                            <span className="text-xs text-slate-400 italic">No audio link</span>
                                                        )}
                                                    </td>
                                                    <td className="py-4 px-6 text-slate-500 dark:text-slate-400 text-xs">
                                                        {music.created_at
                                                            ? new Date(music.created_at).toLocaleDateString("en-US", {
                                                                year: "numeric",
                                                                month: "short",
                                                                day: "numeric"
                                                            })
                                                            : "N/A"}
                                                    </td>
                                                    <td className="py-4 px-6 text-right space-x-1">
                                                        <button
                                                            onClick={() => openEditModal(music)}
                                                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-lg transition-colors inline-flex items-center"
                                                            title="Edit Music"
                                                        >
                                                            <Edit2 className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteMusic(musicId, music.name)}
                                                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors inline-flex items-center"
                                                            title="Delete Music"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ADD / EDIT MUSIC MODAL */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-lg shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                        {/* Modal Header */}
                        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="p-2 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-lg">
                                    <Music className="w-5 h-5" />
                                </div>
                                <h3 className="text-lg font-bold text-slate-800 dark:text-white">
                                    {editingMusic ? "Edit Music" : "Add New Music"}
                                </h3>
                            </div>
                            <button
                                onClick={resetForm}
                                disabled={isSaving}
                                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Form */}
                        <form onSubmit={handleSubmit} className="p-6 space-y-5">
                            {/* Music Name */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
                                    Music Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    placeholder="Enter music title..."
                                    value={musicName}
                                    onChange={(e) => setMusicName(e.target.value)}
                                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:text-white transition-all"
                                />
                            </div>

                            {/* Audio Upload */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
                                    Audio File (.mp3, .wav, .m4a, .aac, .ogg) {!editingMusic && <span className="text-red-500">*</span>}
                                </label>
                                <div
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={handleDrop}
                                    className={`relative border-2 border-dashed rounded-2xl p-6 text-center transition-all ${selectedFile
                                        ? "border-blue-500 bg-blue-50/20 dark:bg-blue-500/5"
                                        : "border-slate-300 dark:border-slate-600 hover:border-blue-400 bg-slate-50/50 dark:bg-slate-900/50"
                                        }`}
                                >
                                    <input
                                        type="file"
                                        accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac"
                                        onChange={handleFileChange}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        disabled={isSaving}
                                    />
                                    {selectedFile ? (
                                        <div className="flex flex-col items-center justify-center space-y-2">
                                            <div className="p-3 bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 rounded-full">
                                                <FileAudio className="w-8 h-8" />
                                            </div>
                                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate max-w-xs">
                                                {selectedFile.name}
                                            </p>
                                            <p className="text-xs text-slate-400">
                                                {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                                            </p>
                                            {previewAudioUrl && (
                                                <div className="mt-2 w-full pt-2">
                                                    <audio controls src={previewAudioUrl} className="w-full h-8" />
                                                </div>
                                            )}
                                            <span className="text-xs text-blue-500 font-medium pt-1">
                                                Click or drop to replace file
                                            </span>
                                        </div>
                                    ) : editingMusic && previewAudioUrl ? (
                                        <div className="flex flex-col items-center justify-center space-y-2">
                                            <div className="p-3 bg-slate-100 dark:bg-slate-700 text-blue-600 dark:text-blue-400 rounded-full">
                                                <FileAudio className="w-8 h-8" />
                                            </div>
                                            <p className="text-xs text-slate-400">Current Audio:</p>
                                            <audio controls src={previewAudioUrl} className="w-full h-8" />
                                            <span className="text-xs text-blue-500 font-medium pt-1">
                                                Click or drop new file to replace audio
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center justify-center py-2 space-y-2">
                                            <div className="p-3 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-full">
                                                <UploadCloud className="w-8 h-8" />
                                            </div>
                                            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                                Click to upload or drag & drop audio here
                                            </p>
                                            <p className="text-xs text-slate-400">
                                                Supports MP3, WAV, M4A, AAC, OGG
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Modal Actions */}
                            <div className="pt-2 flex items-center justify-end gap-3 border-t border-slate-100 dark:border-slate-700">
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    disabled={isSaving}
                                    className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSaving || !musicName.trim() || (!editingMusic && !selectedFile)}
                                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-5 py-2.5 rounded-xl font-medium shadow-md shadow-blue-500/20 text-sm transition-all"
                                >
                                    {isSaving ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            <span>Saving...</span>
                                        </>
                                    ) : (
                                        <span>{editingMusic ? "Update Music" : "Add Music"}</span>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* BULK ADD MUSIC MODAL */}
            {isBulkModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-3xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col max-h-[90vh]">
                        {/* Hidden bulk input for browsing more files */}
                        <input
                            type="file"
                            ref={bulkFileInputRef}
                            multiple
                            accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac"
                            onChange={handleBulkFileChange}
                            className="hidden"
                            disabled={isBulkSaving}
                        />

                        {/* Modal Header */}
                        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-xl">
                                    <UploadCloud className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                        Bulk Add Music
                                        {bulkTracks.length > 0 && (
                                            <span className="text-xs bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded-full font-semibold">
                                                {bulkTracks.length} {bulkTracks.length === 1 ? "track" : "tracks"}
                                            </span>
                                        )}
                                    </h3>
                                    <p className="text-xs text-slate-400">
                                        Upload multiple audio tracks at once. Every file is created as a separate row in the database.
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={closeBulkModal}
                                disabled={isBulkSaving}
                                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 overflow-y-auto space-y-5 flex-1">
                            {/* Drag & Drop Zone */}
                            <div
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={handleBulkDrop}
                                onClick={() => bulkFileInputRef.current && bulkFileInputRef.current.click()}
                                className="relative border-2 border-dashed border-emerald-300 dark:border-emerald-600/50 hover:border-emerald-500 bg-emerald-50/20 dark:bg-emerald-950/10 hover:bg-emerald-50/40 rounded-2xl p-6 text-center cursor-pointer transition-all"
                            >
                                <div className="flex flex-col items-center justify-center space-y-2">
                                    <div className="p-3 bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded-full">
                                        <UploadCloud className="w-8 h-8" />
                                    </div>
                                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                                        Click to select multiple audio files or drag & drop here
                                    </p>
                                    <p className="text-xs text-slate-400">
                                        Select multiple .mp3, .wav, .m4a, .aac, .ogg files at once (up to 100 tracks)
                                    </p>
                                </div>
                            </div>

                            {/* Selected Tracks List */}
                            {bulkTracks.length > 0 && (
                                <div className="space-y-3">
                                    {/* Action Bar for tracks */}
                                    <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900/60 p-3 rounded-xl border border-slate-200/80 dark:border-slate-700">
                                        <div className="flex items-center gap-3 text-xs">
                                            <span className="font-semibold text-slate-700 dark:text-slate-200">
                                                Selected: <span className="text-emerald-600 dark:text-emerald-400 font-bold">{bulkTracks.length}</span>
                                            </span>
                                            <span className="text-slate-300 dark:text-slate-600">•</span>
                                            <span className="text-slate-500">
                                                Total Size: <span className="font-medium text-slate-700 dark:text-slate-300">
                                                    {bulkTracks.reduce((sum, t) => sum + parseFloat(t.size || 0), 0).toFixed(2)} MB
                                                </span>
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => bulkFileInputRef.current && bulkFileInputRef.current.click()}
                                                disabled={isBulkSaving}
                                                className="text-xs text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 font-semibold px-2 py-1 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-950/40 transition-colors"
                                            >
                                                + Add More Files
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleClearAllBulkTracks}
                                                disabled={isBulkSaving}
                                                className="text-xs text-red-500 hover:text-red-700 font-semibold px-2 py-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                                            >
                                                Clear All
                                            </button>
                                        </div>
                                    </div>

                                    {/* Scrollable Tracks Table */}
                                    <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden max-h-72 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-700/60 bg-white dark:bg-slate-800">
                                        {bulkTracks.map((track, idx) => {
                                            const isPlaying = currentlyPlayingId === track.id;
                                            return (
                                                <div
                                                    key={track.id}
                                                    className="p-3 flex items-center gap-3 hover:bg-slate-50/70 dark:hover:bg-slate-700/30 transition-colors"
                                                >
                                                    <span className="text-xs font-semibold text-slate-400 w-6 text-center">
                                                        {idx + 1}
                                                    </span>

                                                    {/* Play Preview Button */}
                                                    <button
                                                        type="button"
                                                        onClick={() => togglePlayMusic(track.id, track.previewUrl)}
                                                        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                                                            isPlaying
                                                                ? "bg-emerald-600 text-white shadow-sm"
                                                                : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-emerald-50 hover:text-emerald-600"
                                                        }`}
                                                        title={isPlaying ? "Pause Preview" : "Play Preview"}
                                                    >
                                                        {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
                                                    </button>

                                                    {/* Editable Music Title */}
                                                    <div className="flex-1 min-w-0">
                                                        <input
                                                            type="text"
                                                            value={track.name}
                                                            onChange={(e) => handleBulkTrackNameChange(track.id, e.target.value)}
                                                            placeholder="Track name..."
                                                            disabled={isBulkSaving}
                                                            className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-1.5 text-xs font-medium text-slate-800 dark:text-slate-100 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                                                        />
                                                        <span className="text-[11px] text-slate-400 truncate block mt-0.5">
                                                            {track.file.name}
                                                        </span>
                                                    </div>

                                                    {/* Size Badge */}
                                                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded-md flex-shrink-0">
                                                        {track.size} MB
                                                    </span>

                                                    {/* Remove Button */}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveBulkTrack(track.id)}
                                                        disabled={isBulkSaving}
                                                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors flex-shrink-0"
                                                        title="Remove track"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/50">
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                                {bulkTracks.length > 0
                                    ? `Ready to upload ${bulkTracks.length} tracks into separate database rows.`
                                    : "No tracks selected yet."}
                            </span>
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={closeBulkModal}
                                    disabled={isBulkSaving}
                                    className="px-4 py-2 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleBulkSubmit}
                                    disabled={isBulkSaving || bulkTracks.length === 0}
                                    className="flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 text-white px-5 py-2 rounded-xl font-medium shadow-md shadow-emerald-500/20 text-sm transition-all"
                                >
                                    {isBulkSaving ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            <span>Uploading {bulkTracks.length} Tracks...</span>
                                        </>
                                    ) : (
                                        <>
                                            <UploadCloud className="w-4 h-4" />
                                            <span>Upload All ({bulkTracks.length})</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
